import React, { useState, useEffect, useMemo } from "react";
import { useTableColumnFilters, FilterTh } from "../components/tableFilters";
import {
  firmService,
  invoiceTypeService,
  cityService,
  districtService,
  taxOfficeService,
} from "../services/dataService";
import {
  Firm,
  InvoiceType,
  FirmType,
  City,
  District,
  TaxOffice,
} from "../types";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import { turkishIncludes } from "../utils/formatters";
import "./Firms.css";

interface FirmFormData {
  firmCode: string;
  firmName: string;
  firmType: string;
  description: string;
  address: string;
  cityId: string;
  districtId: string;
  taxOfficeId: string;
  countryCode: string;
  countryName: string;
  contactPerson: string;
  phone: string;
  email: string;
  taxNumber: string;
  tckn: string;
  firstName: string;
  lastName: string;
  invoiceTypeId: string;
}

const Firms: React.FC = () => {
  const [firms, setFirms] = useState<Firm[]>([]);
  const [invoiceTypes, setInvoiceTypes] = useState<InvoiceType[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [taxOffices, setTaxOffices] = useState<TaxOffice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [districtSearchTerm, setDistrictSearchTerm] = useState("");
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingFirm, setEditingFirm] = useState<Firm | null>(null);
  const [formData, setFormData] = useState<FirmFormData>({
    firmCode: "",
    firmName: "",
    firmType: "0",
    description: "",
    address: "",
    cityId: "",
    districtId: "",
    taxOfficeId: "",
    countryCode: "TR",
    countryName: "Türkiye",
    contactPerson: "",
    phone: "",
    email: "",
    taxNumber: "",
    tckn: "",
    firstName: "",
    lastName: "",
    invoiceTypeId: "",
  });
  const [formErrors, setFormErrors] = useState<Partial<FirmFormData>>({});

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Confirm Modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  useEffect(() => {
    loadFirms();
    loadInvoiceTypes();
    loadCities();
  }, []);

  const loadFirms = async () => {
    try {
      setLoading(true);
      const data = await firmService.getFirms();
      setFirms(data);
    } catch (error) {
      console.error("❌ Firmalar yüklenirken hata:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadInvoiceTypes = async () => {
    try {
      const data = await invoiceTypeService.getInvoiceTypes();
      setInvoiceTypes(data);
    } catch (error) {
      console.error("❌ Fatura tipleri yüklenirken hata:", error);
    }
  };

  const loadCities = async () => {
    try {
      const data = await cityService.getCities();
      setCities(data);
    } catch (error) {
      console.error("❌ İller yüklenirken hata:", error);
    }
  };

  const loadDistrictsByCity = async (cityId: string) => {
    try {
      const data = await districtService.getDistrictsByCity(cityId);
      setDistricts(data);
    } catch (error) {
      console.error("❌ İlçeler yüklenirken hata:", error);
    }
  };

  const loadTaxOfficesByCity = async (cityId: string) => {
    try {
      const data = await taxOfficeService.getTaxOfficesByCity(cityId);
      setTaxOffices(data);
    } catch (error) {
      console.error("❌ Vergi daireleri yüklenirken hata:", error);
    }
  };

  const handleCityChange = (cityId: number | string) => {
    const cityIdStr = cityId.toString();
    setFormData({
      ...formData,
      cityId: cityIdStr,
      districtId: "",
      taxOfficeId: "",
    });
    if (cityId) {
      loadDistrictsByCity(cityIdStr);
      loadTaxOfficesByCity(cityIdStr);
    } else {
      setDistricts([]);
      setTaxOffices([]);
    }
  };

  const handleOpenModal = async (firm?: Firm) => {
    if (firm) {
      setEditingFirm(firm);
      setFormData({
        firmCode: firm.firmCode,
        firmName: firm.firmName,
        firmType: firm.firmType?.toString() || "0",
        description: firm.description || "",
        address: firm.address || "",
        cityId: firm.cityId?.toString() || "",
        districtId: firm.districtId?.toString() || "",
        taxOfficeId: firm.taxOfficeId?.toString() || "",
        countryCode: firm.countryCode || "TR",
        countryName: firm.countryName || "Türkiye",
        contactPerson: firm.contactPerson || "",
        phone: firm.phone || "",
        email: firm.email || "",
        taxNumber: firm.taxNumber || "",
        tckn: firm.tckn || "",
        firstName: firm.firstName || "",
        lastName: firm.lastName || "",
        invoiceTypeId: firm.invoiceTypeId?.toString() || "",
      });

      // Arama terimleri için şehir ve ilçe isimlerini set et
      setCitySearchTerm(firm.city?.cityName || "");
      setDistrictSearchTerm(firm.district?.districtName || "");

      // Eğer city seçiliyse, ilişkili district ve taxOffice'leri yükle
      if (firm.cityId) {
        await loadDistrictsByCity(firm.cityId.toString());
        await loadTaxOfficesByCity(firm.cityId.toString());
      }
    } else {
      setEditingFirm(null);
      setFormData({
        firmCode: "",
        firmName: "",
        firmType: "0",
        description: "",
        address: "",
        cityId: "",
        districtId: "",
        taxOfficeId: "",
        countryCode: "TR",
        countryName: "Türkiye",
        contactPerson: "",
        phone: "",
        email: "",
        taxNumber: "",
        tckn: "",
        firstName: "",
        lastName: "",
        invoiceTypeId: "",
      });
      setCitySearchTerm("");
      setDistrictSearchTerm("");
      setDistricts([]);
      setTaxOffices([]);
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingFirm(null);
    setFormData({
      firmCode: "",
      firmName: "",
      firmType: "0",
      description: "",
      address: "",
      cityId: "",
      districtId: "",
      taxOfficeId: "",
      countryCode: "TR",
      countryName: "Türkiye",
      contactPerson: "",
      phone: "",
      email: "",
      taxNumber: "",
      tckn: "",
      firstName: "",
      lastName: "",
      invoiceTypeId: "",
    });
    setCitySearchTerm("");
    setDistrictSearchTerm("");
    setDistricts([]);
    setTaxOffices([]);
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: Partial<FirmFormData> = {};

    if (!formData.firmCode.trim()) {
      errors.firmCode = "Firma kodu zorunludur";
    }

    if (!formData.firmName.trim()) {
      errors.firmName = "Firma adı zorunludur";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Form verilerini backend'e uygun formata çevir
      // Boş string'leri null'a çevir ve ID'leri integer'a dönüştür
      const firmData: any = {
        firmCode: formData.firmCode,
        firmName: formData.firmName,
        firmType: parseInt(formData.firmType) as FirmType,
        description: formData.description || null,
        address: formData.address || null,
        cityId: formData.cityId ? parseInt(formData.cityId) : null,
        districtId: formData.districtId ? parseInt(formData.districtId) : null,
        taxOfficeId: formData.taxOfficeId
          ? parseInt(formData.taxOfficeId)
          : null,
        countryCode: formData.countryCode || null,
        countryName: formData.countryName || null,
        contactPerson: formData.contactPerson || null,
        phone: formData.phone || null,
        email: formData.email || null,
        taxNumber: formData.taxNumber || null,
        tckn: formData.tckn || null,
        firstName: formData.firstName || null,
        lastName: formData.lastName || null,
        invoiceTypeId: formData.invoiceTypeId
          ? parseInt(formData.invoiceTypeId)
          : null,
      };

      if (editingFirm) {
        // Güncelleme - firmId'yi de ekle
        await firmService.updateFirm(editingFirm.firmId, {
          ...firmData,
          firmId: editingFirm.firmId,
        });
      } else {
        // Yeni firma oluşturma
        await firmService.createFirm(firmData);
      }

      handleCloseModal();
      loadFirms();
    } catch (error: any) {
      console.error("❌ Firma kaydedilirken hata:", error);

      // Backend'den gelen error mesajını string'e çevir
      let errorMessage = "Firma kaydedilirken bir hata oluştu.";
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === "string") {
          errorMessage = errorData;
        } else if (errorData.title || errorData.detail) {
          errorMessage = errorData.title || errorData.detail;
        } else if (errorData.errors) {
          // ASP.NET Core validation errors
          const validationErrors = Object.entries(errorData.errors)
            .map(
              ([field, messages]: [string, any]) =>
                `${field}: ${Array.isArray(messages) ? messages.join(", ") : messages}`,
            )
            .join("\n");
          errorMessage = validationErrors;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setAlertModal({
        isOpen: true,
        message: errorMessage,
        type: "error",
      });
    }
  };

  const handleDelete = async (firm: Firm) => {
    setConfirmModal({
      isOpen: true,
      message: `"${firm.firmName}" firmasını silmek istediğinizden emin misiniz?`,
      type: "danger",
      onConfirm: async () => {
        try {
          await firmService.deleteFirm(firm.firmId);
          loadFirms();
        } catch (error: any) {
          console.error("❌ Firma silinirken hata:", error);

          // Backend'den gelen error mesajını string'e çevir
          let errorMessage = "Firma silinirken bir hata oluştu.";
          if (error.response?.data) {
            const errorData = error.response.data;
            if (typeof errorData === "string") {
              errorMessage = errorData;
            } else if (errorData.title || errorData.detail) {
              errorMessage = errorData.title || errorData.detail;
            } else if (errorData.errors) {
              // ASP.NET Core validation errors
              const validationErrors = Object.entries(errorData.errors)
                .map(
                  ([field, messages]: [string, any]) =>
                    `${field}: ${Array.isArray(messages) ? messages.join(", ") : messages}`,
                )
                .join("\n");
              errorMessage = validationErrors;
            }
          } else if (error.message) {
            errorMessage = error.message;
          }

          setAlertModal({
            isOpen: true,
            message: errorMessage,
            type: "error",
          });
        }
      },
    });
  };

  const filteredFirms = firms.filter(
    (firm) =>
      turkishIncludes(firm.firmName, searchTerm) ||
      turkishIncludes(firm.firmCode, searchTerm) ||
      turkishIncludes(firm.contactPerson || "", searchTerm) ||
      turkishIncludes(firm.phone || "", searchTerm) ||
      turkishIncludes(firm.email || "", searchTerm),
  );

  const firmColDefs = useMemo(
    () => [
      { key: "code", value: (f: Firm) => f.firmCode },
      { key: "name", value: (f: Firm) => f.firmName },
      { key: "type", value: (f: Firm) => (f.firmType === 1 ? "Gerçek Kişi" : "Tüzel Kişi") },
      { key: "invoice", value: (f: Firm) => f.invoiceType?.name || "" },
      { key: "contact", value: (f: Firm) => f.contactPerson || "" },
      { key: "city", value: (f: Firm) => f.city?.cityName || "" },
      { key: "tax", value: (f: Firm) => (f.firmType === 1 ? f.tckn : f.taxNumber) || "" },
    ],
    [],
  );
  const { ctx: firmCf, filtered: displayFirms } = useTableColumnFilters(filteredFirms, firmColDefs);

  if (loading) {
    return <PageLoader message="Firmalar yükleniyor..." />;
  }

  return (
    <div className="firms-container">
      {/* Header */}
      <div className="firms-header">
        <div className="header-content">
          <h1>Firma Yönetimi</h1>
        </div>
        <button className="btn btn-primary" onClick={() => handleOpenModal()}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Yeni Firma Ekle
        </button>
      </div>

      {/* Search Bar */}
      <div className="search-bar">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#666"
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
        <input
          type="text"
          placeholder="Firma adı, kodu veya yetkili ile ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Firms Table */}
      <div className="firms-table-container">
        {displayFirms.length === 0 ? (
          <div className="empty-state">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ccc"
              strokeWidth="2"
            >
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
            <p>
              {searchTerm
                ? "Arama kriterlerine uygun firma bulunamadı"
                : "Henüz firma eklenmemiş"}
            </p>
          </div>
        ) : (
          <table className="firms-table">
            <thead>
              <tr>
                <FilterTh label="Firma Kodu" colKey="code" ctx={firmCf} />
                <FilterTh label="Firma Adı" colKey="name" ctx={firmCf} />
                <FilterTh label="Tipi" colKey="type" ctx={firmCf} />
                <FilterTh label="Fatura Tipi" colKey="invoice" ctx={firmCf} />
                <FilterTh label="Yetkili / Telefon" colKey="contact" ctx={firmCf} />
                <FilterTh label="İl / İlçe" colKey="city" ctx={firmCf} />
                <FilterTh label="Vergi No / TCKN" colKey="tax" ctx={firmCf} />
                <th className="actions-column">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {displayFirms.map((firm) => (
                <tr key={firm.firmId}>
                  <td data-label="Firma Kodu">
                    <span className="firm-code-badge">{firm.firmCode}</span>
                  </td>
                  <td data-label="Firma Adı">
                    <strong>{firm.firmName}</strong>
                    {firm.description && (
                      <div className="firm-description">{firm.description}</div>
                    )}
                  </td>
                  <td data-label="Tipi">
                    <span
                      className={`firm-type-badge ${firm.firmType === 1 ? "gercek" : "tuzel"}`}
                    >
                      {firm.firmType === 1 ? "Gerçek Kişi" : "Tüzel Kişi"}
                    </span>
                  </td>
                  <td data-label="Fatura Tipi">
                    {firm.invoiceType ? (
                      <>
                        <div>{firm.invoiceType.name}</div>
                        {firm.invoiceType.description && (
                          <div className="firm-sub-text">
                            {firm.invoiceType.description}
                          </div>
                        )}
                      </>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td data-label="Yetkili / Telefon">
                    <div>{firm.contactPerson || "-"}</div>
                    {firm.phone && (
                      <div className="firm-sub-text">{firm.phone}</div>
                    )}
                  </td>
                  <td data-label="İl / İlçe">
                    <div>{firm.city?.cityName || "-"}</div>
                    {firm.district && (
                      <div className="firm-sub-text">
                        {firm.district.districtName}
                      </div>
                    )}
                  </td>
                  <td data-label="Vergi No / TCKN">
                    {firm.firmType === 1 ? (
                      <>
                        <div>{firm.tckn || "-"}</div>
                        {(firm.firstName || firm.lastName) && (
                          <div className="firm-sub-text">
                            {firm.firstName} {firm.lastName}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div>{firm.taxNumber || "-"}</div>
                        {firm.taxOffice && (
                          <div className="firm-sub-text">
                            {firm.taxOffice.name}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="actions-column" data-label="İşlemler">
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleOpenModal(firm)}
                        title="Düzenle"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                        Düzenle
                      </button>
                      <button
                        className="btn btn-sm btn-soft-danger"
                        onClick={() => handleDelete(firm)}
                        title="Sil"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                        Sil
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingFirm ? "Firma Düzenle" : "Yeni Firma Ekle"}</h2>
              <button className="modal-close" onClick={handleCloseModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Temel Bilgiler */}
                <div className="form-section">
                  <h3 className="form-section-title">Temel Bilgiler</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        Firma Kodu <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.firmCode}
                        onChange={(e) =>
                          setFormData({ ...formData, firmCode: e.target.value })
                        }
                        placeholder="Örn: FRM001"
                        className={formErrors.firmCode ? "error" : ""}
                      />
                      {formErrors.firmCode && (
                        <span className="error-message">
                          {formErrors.firmCode}
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>
                        Firma Adı <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.firmName}
                        onChange={(e) =>
                          setFormData({ ...formData, firmName: e.target.value })
                        }
                        placeholder="Firma adını girin"
                        className={formErrors.firmName ? "error" : ""}
                      />
                      {formErrors.firmName && (
                        <span className="error-message">
                          {formErrors.firmName}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Firma Tipi</label>
                      <select
                        value={formData.firmType}
                        onChange={(e) =>
                          setFormData({ ...formData, firmType: e.target.value })
                        }
                      >
                        <option value="0">Tüzel Kişi (Şirket)</option>
                        <option value="1">Gerçek Kişi (Şahıs)</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Fatura Tipi</label>
                      <select
                        value={formData.invoiceTypeId}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            invoiceTypeId: e.target.value,
                          })
                        }
                      >
                        <option value="">Fatura Tipi Seçin</option>
                        {invoiceTypes.map((type) => (
                          <option
                            key={type.invoiceTypeId}
                            value={type.invoiceTypeId}
                          >
                            {type.name}
                            {type.description ? ` - ${type.description}` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Açıklama</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      placeholder="Firma hakkında açıklama"
                      rows={2}
                    />
                  </div>
                </div>

                {/* Vergi Bilgileri - Tüzel Kişi */}
                {formData.firmType === "0" && (
                  <div className="form-section">
                    <h3 className="form-section-title">Vergi Bilgileri</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Vergi Numarası</label>
                        <input
                          type="text"
                          value={formData.taxNumber}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              taxNumber: e.target.value,
                            })
                          }
                          placeholder="Vergi numarası (10 hane)"
                          maxLength={10}
                        />
                      </div>

                      <div className="form-group">
                        <label>E-posta</label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) =>
                            setFormData({ ...formData, email: e.target.value })
                          }
                          placeholder="firma@example.com"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Kimlik Bilgileri - Gerçek Kişi */}
                {formData.firmType === "1" && (
                  <div className="form-section">
                    <h3 className="form-section-title">Kimlik Bilgileri</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label>TC Kimlik Numarası</label>
                        <input
                          type="text"
                          value={formData.tckn}
                          onChange={(e) =>
                            setFormData({ ...formData, tckn: e.target.value })
                          }
                          placeholder="TC Kimlik No (11 hane)"
                          maxLength={11}
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Ad</label>
                        <input
                          type="text"
                          value={formData.firstName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              firstName: e.target.value,
                            })
                          }
                          placeholder="Ad"
                        />
                      </div>

                      <div className="form-group">
                        <label>Soyad</label>
                        <input
                          type="text"
                          value={formData.lastName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              lastName: e.target.value,
                            })
                          }
                          placeholder="Soyad"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* İletişim Bilgileri */}
                <div className="form-section">
                  <h3 className="form-section-title">İletişim Bilgileri</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Yetkili Kişi</label>
                      <input
                        type="text"
                        value={formData.contactPerson}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            contactPerson: e.target.value,
                          })
                        }
                        placeholder="Yetkili adı"
                      />
                    </div>

                    <div className="form-group">
                      <label>Telefon</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData({ ...formData, phone: e.target.value })
                        }
                        placeholder="05XX XXX XX XX"
                      />
                    </div>
                  </div>
                </div>

                {/* Adres Bilgileri */}
                <div className="form-section">
                  <h3 className="form-section-title">Adres Bilgileri</h3>

                  <div className="form-group">
                    <label>Adres</label>
                    <textarea
                      value={formData.address}
                      onChange={(e) =>
                        setFormData({ ...formData, address: e.target.value })
                      }
                      placeholder="Sokak, mahalle, bina no vb."
                      rows={2}
                      maxLength={500}
                    />
                  </div>

                  <div className="location-input-group">
                    {/* İl Seçimi */}
                    <div className="location-field">
                      <label>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                          <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                        İl
                      </label>
                      <div className="autocomplete-wrapper">
                        <div className="autocomplete-input-container">
                          <input
                            type="text"
                            value={citySearchTerm}
                            onChange={(e) => {
                              const searchValue = e.target.value;
                              setCitySearchTerm(searchValue);
                              setShowCityDropdown(true);

                              // Eğer tam eşleşme varsa ID'yi set et
                              const matchedCity = cities.find(
                                (city) =>
                                  city.cityName?.toLowerCase() ===
                                  searchValue?.toLowerCase(),
                              );

                              if (matchedCity) {
                                handleCityChange(matchedCity.cityId);
                              } else if (!searchValue) {
                                handleCityChange("");
                              }
                            }}
                            onFocus={() => setShowCityDropdown(true)}
                            onBlur={() => {
                              setTimeout(() => {
                                setShowCityDropdown(false);
                                // Geçerli bir şehir seçilmemişse temizle
                                const matchedCity = cities.find(
                                  (city) =>
                                    city.cityName?.toLowerCase() ===
                                    citySearchTerm?.toLowerCase(),
                                );
                                if (!matchedCity && citySearchTerm) {
                                  setCitySearchTerm("");
                                  handleCityChange("");
                                }
                              }, 200);
                            }}
                            placeholder="İl arayın..."
                            className={formData.cityId ? "has-value" : ""}
                          />
                          {citySearchTerm && (
                            <button
                              type="button"
                              className="autocomplete-clear"
                              onClick={() => {
                                setCitySearchTerm("");
                                handleCityChange("");
                                setShowCityDropdown(false);
                              }}
                            >
                              ×
                            </button>
                          )}
                          <span className="autocomplete-icon">
                            {formData.cityId ? (
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            ) : (
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                              </svg>
                            )}
                          </span>
                        </div>

                        {showCityDropdown && (
                          <div className="autocomplete-dropdown">
                            {cities
                              .filter(
                                (city) =>
                                  !citySearchTerm ||
                                  city.cityName
                                    ?.toLowerCase()
                                    .includes(citySearchTerm.toLowerCase()),
                              )
                              .slice(0, 10)
                              .map((city) => (
                                <div
                                  key={city.cityId}
                                  className={`autocomplete-option ${formData.cityId === city.cityId.toString() ? "selected" : ""}`}
                                  onMouseDown={() => {
                                    setCitySearchTerm(city.cityName || "");
                                    handleCityChange(city.cityId);
                                    setShowCityDropdown(false);
                                  }}
                                >
                                  <span className="autocomplete-option-text">
                                    {city.cityName}
                                  </span>
                                  <span className="autocomplete-option-check">
                                    ✓
                                  </span>
                                </div>
                              ))}
                            {cities.filter(
                              (city) =>
                                !citySearchTerm ||
                                city.cityName
                                  ?.toLowerCase()
                                  .includes(citySearchTerm.toLowerCase()),
                            ).length === 0 && (
                              <div className="autocomplete-no-results">
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <circle cx="11" cy="11" r="8"></circle>
                                  <path d="m21 21-4.35-4.35"></path>
                                </svg>
                                <div>Sonuç bulunamadı</div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {formData.cityId && (
                        <div className="location-badge">
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          {citySearchTerm} seçildi
                        </div>
                      )}
                    </div>

                    {/* İlçe Seçimi */}
                    <div className="location-field">
                      <label>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect
                            x="3"
                            y="3"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          ></rect>
                          <line x1="3" y1="9" x2="21" y2="9"></line>
                          <line x1="9" y1="21" x2="9" y2="9"></line>
                        </svg>
                        İlçe
                      </label>
                      <div className="autocomplete-wrapper">
                        <div className="autocomplete-input-container">
                          <input
                            type="text"
                            value={districtSearchTerm}
                            onChange={(e) => {
                              const searchValue = e.target.value;
                              setDistrictSearchTerm(searchValue);
                              setShowDistrictDropdown(true);

                              // Eğer tam eşleşme varsa ID'yi set et
                              const matchedDistrict = districts.find(
                                (district) =>
                                  district.districtName?.toLowerCase() ===
                                  searchValue?.toLowerCase(),
                              );

                              if (matchedDistrict) {
                                setFormData({
                                  ...formData,
                                  districtId:
                                    matchedDistrict.districtId.toString(),
                                });
                              } else if (!searchValue) {
                                setFormData({ ...formData, districtId: "" });
                              }
                            }}
                            onFocus={() =>
                              formData.cityId && setShowDistrictDropdown(true)
                            }
                            onBlur={() => {
                              setTimeout(() => {
                                setShowDistrictDropdown(false);
                                // Geçerli bir ilçe seçilmemişse temizle
                                const matchedDistrict = districts.find(
                                  (district) =>
                                    district.districtName?.toLowerCase() ===
                                    districtSearchTerm?.toLowerCase(),
                                );
                                if (!matchedDistrict && districtSearchTerm) {
                                  setDistrictSearchTerm("");
                                  setFormData({ ...formData, districtId: "" });
                                }
                              }, 200);
                            }}
                            placeholder={
                              formData.cityId
                                ? "İlçe arayın..."
                                : "Önce il seçin"
                            }
                            disabled={!formData.cityId}
                            className={formData.districtId ? "has-value" : ""}
                          />
                          {districtSearchTerm && formData.cityId && (
                            <button
                              type="button"
                              className="autocomplete-clear"
                              onClick={() => {
                                setDistrictSearchTerm("");
                                setFormData({ ...formData, districtId: "" });
                                setShowDistrictDropdown(false);
                              }}
                            >
                              ×
                            </button>
                          )}
                          <span className="autocomplete-icon">
                            {formData.districtId ? (
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            ) : (
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                              </svg>
                            )}
                          </span>
                        </div>

                        {showDistrictDropdown && formData.cityId && (
                          <div className="autocomplete-dropdown">
                            {districts
                              .filter(
                                (district) =>
                                  !districtSearchTerm ||
                                  district.districtName
                                    ?.toLowerCase()
                                    .includes(districtSearchTerm.toLowerCase()),
                              )
                              .slice(0, 10)
                              .map((district) => (
                                <div
                                  key={district.districtId}
                                  className={`autocomplete-option ${formData.districtId === district.districtId.toString() ? "selected" : ""}`}
                                  onMouseDown={() => {
                                    setDistrictSearchTerm(
                                      district.districtName || "",
                                    );
                                    setFormData({
                                      ...formData,
                                      districtId:
                                        district.districtId.toString(),
                                    });
                                    setShowDistrictDropdown(false);
                                  }}
                                >
                                  <span className="autocomplete-option-text">
                                    {district.districtName}
                                  </span>
                                  <span className="autocomplete-option-check">
                                    ✓
                                  </span>
                                </div>
                              ))}
                            {districts.filter(
                              (district) =>
                                !districtSearchTerm ||
                                district.districtName
                                  ?.toLowerCase()
                                  .includes(districtSearchTerm.toLowerCase()),
                            ).length === 0 && (
                              <div className="autocomplete-no-results">
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <circle cx="11" cy="11" r="8"></circle>
                                  <path d="m21 21-4.35-4.35"></path>
                                </svg>
                                <div>Sonuç bulunamadı</div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {formData.districtId && (
                        <div className="location-badge">
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          {districtSearchTerm} seçildi
                        </div>
                      )}
                    </div>
                  </div>

                  {formData.firmType === "0" && (
                    <div className="form-group">
                      <label>Vergi Dairesi</label>
                      <select
                        value={formData.taxOfficeId}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            taxOfficeId: e.target.value,
                          })
                        }
                        disabled={!formData.cityId}
                      >
                        <option value="">
                          {formData.cityId
                            ? "Vergi dairesi seçin"
                            : "Önce il seçin"}
                        </option>
                        {taxOffices.map((office) => (
                          <option
                            key={office.taxOfficeId}
                            value={office.taxOfficeId}
                          >
                            {office.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="form-row">
                    <div className="form-group">
                      <label>Ülke Kodu</label>
                      <input
                        type="text"
                        value={formData.countryCode}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            countryCode: e.target.value,
                          })
                        }
                        placeholder="TR"
                        maxLength={3}
                      />
                    </div>

                    <div className="form-group">
                      <label>Ülke</label>
                      <input
                        type="text"
                        value={formData.countryName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            countryName: e.target.value,
                          })
                        }
                        placeholder="Türkiye"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingFirm ? "Güncelle" : "Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
};

export default Firms;
